import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { PieChart as PieIcon } from "lucide-react";

import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

// FIN-5 — DistribuicaoCategoriasDonut.
// Donut + lista de categorias com barras de progresso coloridas.
// Consome breakdown.breakdown direto do hook.

const fmtBRL = (v) => Number(v ?? 0).toLocaleString("pt-BR", {
  style: "currency", currency: "BRL",
});

function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-surface-overlay border border-subtle rounded-lg px-3 py-2 shadow-2xl">
      <p className="text-caption font-semibold text-foreground mb-0.5">
        {d.icon} {d.name}
      </p>
      <p className="text-micro text-foreground-muted tabular-nums">
        {fmtBRL(d.total_current)} ({d.pct_of_total}%)
      </p>
    </div>
  );
}

export default function DistribuicaoCategoriasDonut({ breakdown, isLoading }) {
  const items = (breakdown?.breakdown || []).filter(b => b.total_current > 0);
  const totalSpent = Number(breakdown?.total_spent ?? 0);

  return (
    <Card variant="elevated" className="glass-card glow-hover p-5 md:p-6 min-h-[420px] flex flex-col" data-testid="cat-donut">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-7 h-7 rounded-md bg-brand-cyan/15 flex items-center justify-center shrink-0">
          <PieIcon className="w-4 h-4 text-brand-cyan" aria-hidden="true" />
        </div>
        <h3
          className="text-caption font-semibold uppercase text-foreground-muted"
          style={{ letterSpacing: "0.10em" }}
        >
          Distribuição da carteira
        </h3>
      </div>

      {items.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 text-foreground-muted">
          <PieIcon className="w-8 h-8 opacity-40" aria-hidden="true" />
          <p className="text-caption">
            {isLoading ? "carregando…" : "Sem despesas neste mês"}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-4 flex-1">
          {/* Donut */}
          <div className="w-full h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={items}
                  dataKey="total_current"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={48}
                  outerRadius={78}
                  paddingAngle={2}
                  stroke="rgba(0,0,0,0.4)"
                  strokeWidth={2}
                >
                  {items.map((c) => (
                    <Cell key={c.name} fill={c.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Lista com barras */}
          <div className="space-y-2 max-h-[260px] overflow-y-auto pr-1">
            {items.map(b => (
              <div
                key={b.name}
                className="space-y-1"
                data-testid={`cat-donut-row-${b.name.toLowerCase().replace(/\s/g, "-")}`}
              >
                <div className="flex items-center justify-between gap-2 text-caption">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span aria-hidden="true">{b.icon}</span>
                    <span className="truncate text-foreground">{b.name}</span>
                  </div>
                  <span className="font-mono tabular-nums text-foreground-muted shrink-0">
                    {fmtBRL(b.total_current)}
                  </span>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden bg-white/5">
                  <div
                    className={cn("h-full rounded-full transition-all duration-500 ease-out")}
                    style={{
                      width: `${Math.max(2, b.pct_of_total)}%`,
                      backgroundColor: b.color,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between border-t border-subtle/40 pt-2 mt-auto">
            <span className="text-micro text-foreground-muted uppercase font-semibold" style={{ letterSpacing: "0.08em" }}>
              Total
            </span>
            <span className="font-mono font-semibold text-foreground tabular-nums">
              {fmtBRL(totalSpent)}
            </span>
          </div>
        </div>
      )}
    </Card>
  );
}
